import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FaInfoCircle, FaChalkboardTeacher, FaBook, FaDollarSign, FaEnvelope } from 'react-icons/fa';
import PromoBanner from './PromoBanner';
import { FloatingNav } from './FloatingNav'; // Import the new FloatingNav component

const Navbar = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isLoading, setIsLoading] = useState(false);  // For showing loader
  const [error, setError] = useState(null);  // For error messages
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const NAV_LINKS = [
    { href: '/about', key: 'About us', label: 'About Us', icon: <FaInfoCircle /> }, 
    { href: '/courses', key: 'Courses', label: 'Courses', icon: <FaChalkboardTeacher /> }, 
    { href: '/resources', key: 'Resources', label: 'Resources', icon: <FaBook /> }, 
    { href: '/pricing', key: 'Pricing', label: 'Pricing', icon: <FaDollarSign /> }, 
    { href: '/contact', key: 'Contact_us', label: 'Contact Us', icon: <FaEnvelope /> }, 
  ];

  // Check localStorage to see if the banner should be shown or not
  useEffect(() => {
    const bannerClosed = localStorage.getItem('bannerClosed');
    if (bannerClosed) {
      setShowBanner(false);
    }
  }, []);

  // Handle closing the banner and storing it in localStorage
  const handleCloseBanner = () => {
    setShowBanner(false);
    localStorage.setItem('bannerClosed', 'true');
  };

  // Handle login redirect
  const handleLogin = () => {
    setMenuOpen(false);
    setIsLoading(true);
    setError(null);
  
    window.location.href = 'https://server.oracy.uk/auth/google'; 
  
  };
  

  // Define navItems for FloatingNav
  const navItems = NAV_LINKS.map((link) => ({
    name: link.label,
    link: link.href,
    icon: link.icon
  }));

  return (
    <>
      {/* Show error message if any */}
      {error && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[2001]">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-600 text-white rounded-full">
                <svg className="w-6 h-6" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16Zm0 2a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM10 6a1 1 0 0 1 1 1v4a1 1 0 0 1-2 0V7a1 1 0 0 1 1-1Zm0 6a1 1 0 0 1 1 1v2a1 1 0 0 1-2 0v-2a1 1 0 0 1 1-1Z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="text-lg font-semibold text-red-600">Error</div>
            </div>
            <p className="text-base text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => setError(null)}
              className="w-full bg-red-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-red-700 transition-all duration-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* CTA Promotional Banner */}
      {showBanner && <PromoBanner onClose={handleCloseBanner} />}
      <div style={{ paddingTop: showBanner ? '60px' : '0px' }}>
        {/* Main content goes here */}
      </div>

      {/* Primary Navbar - Always Visible */}
      <nav className="relative w-full px-4 md:px-8 lg:px-16 xl:px-[17rem] flex flex-col md:flex-row justify-between items-center z-40 transition-all duration-500 ease-in-out bg-white shadow-md py-3">
        <div className="flex justify-between items-center w-full">
          <a href="/" className="flex-shrink-0">
            <img src="/Logodark.svg" alt="logo" width={160} height={100} />
          </a>
          {/* Hamburger Icon for Primary Navbar */}
          <button
            onClick={toggleMenu}
            className="md:hidden text-black focus:outline-none transition-transform duration-300"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
            </svg>
          </button>
        </div>

        {/* Desktop Menu Links */}
        <ul className="hidden md:flex md:items-center font-semibold md:gap-8 lg:gap-12 xl-1500:gap-16">
          {NAV_LINKS.map((link) => (
            <li key={link.key} className="flex items-center whitespace-nowrap py-2 md:py-0 text-lg">
              <a href={link.href} className="text-black cursor-pointer pb-1.5 hover:text-[#F7941D] hover:font-semibold">
                {link.label}
              </a>
            </li>
          ))}
          {/* Add Login Button */}
          <li>
            <button
              onClick={handleLogin}
              className="bg-[#F7941D] text-white font-semibold px-6 py-2 rounded-md hover:bg-orange-600 transition-all duration-300"
            >
              Login
            </button>
          </li>
        </ul>
      </nav>

      {/* Floating Navbar Component */}
      <FloatingNav navItems={navItems} />

      {/* Mobile Sliding Menu */}
      <div
        className={`fixed md:hidden right-0 top-0 h-full w-full bg-gray-900 text-white z-[2000] transform transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col items-center py-8">
          <button
            onClick={toggleMenu}
            className="absolute top-4 right-4 text-white focus:outline-none"
            aria-label="Close menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          <a href="/" className="mb-6">
            <img src="/logo.svg" alt="logo" width={140} height={100} />
          </a>
          <ul className="flex flex-col items-center space-y-6">
            {NAV_LINKS.map((link) => (
              <li key={link.key}>
                <a
                  href={link.href}
                  className="text-white text-lg"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              </li>
            ))}
            {/* Add Login Button */}
            <li>
              <button
                onClick={handleLogin}
                className="bg-[#F7941D] text-white font-semibold px-6 py-2 rounded-md hover:bg-orange-600 transition-all duration-300"
              >
                Login
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Small Dialog Loader */}
      {isLoading && (
        <div className="fixed inset-0 flex justify-center items-center z-[2001]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-xs w-full text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 border-4 border-t-4 border-[#F7941D] rounded-full animate-spin"></div>
            </div>
            <p className="text-lg text-black">You are being redirected to the login screen, please wait...</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
